import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {DropdownOptionProps} from 'wix-ui-tpa/cssVars'
import {useBi, useEnvironment, useTranslation} from '@wix/yoshi-flow-editor'
import {zonedTimeToUtc} from '@wix/table-reservations-lib/timezone'
import {
  ApprovalMode,
  ReservationLocation,
} from '@wix/ambassador-table-reservations-v1-reservation-location/types'
import {setHMToDate} from '@wix/table-reservations-lib/schedule'
import {Status} from '@wix/ambassador-table-reservations-v1-time-slot/types'

import {
  getInitialReservationLocation,
  getReservationLocationById,
  getReservationLocationsOptions,
} from '../../../../../utils/reservationLocation'
import {
  getInitialPartySizeFromOptions,
  getPartySizeOptionsForReservationLocation,
} from '../../../../../utils/partySize'
import {filterDate, getNearestWorkingDate} from '../../../../../utils/date'
import {getBusinessScheduleFromReservationLocation} from '../../../../../utils/businessSchedule'
import {findTimeOption, getTimeOptions, TimeOption} from '../../../../../utils/timeOptions'
import {useReservationAddonStorage} from '../../../storage'
import {FieldName, getLogger, PageType} from '../../../../../utils/getLogger'
import {useElementWidth} from '../../../../../utils/useElementWidth'
import {getReservationAddOnDataHookWithId} from '../../constants'
import {
  ANY_AREA_ID,
  getAreaOptions,
  getVisibleAreas,
  normalizeAreaId,
} from '../../../../../utils/areas'
import {FormLayout} from '../../../../../types/createStylesParams'

export const useHooks = (
  enabledReservationLocations: ReservationLocation[],
  effectiveFormLayout: FormLayout,
) => {
  const {
    goToReservationsPage,
    goToDetailsPage,
    regionalSettings,
    compId,
    isSiteBusinessPremium,
    getDayTimeSlots,
    dayTimeSlots,
  } = useReservationAddonStorage()
  // reservations unavailable
  const [shouldShowReservationsUnavailable, setShouldShowReservationsUnavailable] = useState(false)

  const handleHideReservationsUnavailable = useCallback(() => {
    setShouldShowReservationsUnavailable(false)
  }, [])

  const {isPreview, isSSR} = useEnvironment()

  const bi = useBi()
  const logger = getLogger(bi)

  const {t} = useTranslation()
  const {isMobile} = useEnvironment()

  const reservationAddOnDataHookWithId = useMemo(
    () => getReservationAddOnDataHookWithId(compId),
    [compId],
  )

  const {width: submitButtonContentWidth, readAndSetWidth: readAndSetSubmitButtonContentWidth} =
    useElementWidth<HTMLButtonElement>(reservationAddOnDataHookWithId)
  const [isLoading, setIsLoading] = useState(false)

  const [selectedReservationLocation, setSelectedReservationLocation] = useState<
    ReservationLocation | undefined
  >(getInitialReservationLocation(enabledReservationLocations))

  useEffect(() => {
    setSelectedReservationLocation((currentLocation) => {
      if (currentLocation?.id) {
        const refreshedLocation = getReservationLocationById(
          currentLocation.id,
          enabledReservationLocations,
        )

        if (refreshedLocation) {
          return refreshedLocation
        }
      }

      return getInitialReservationLocation(enabledReservationLocations)
    })
  }, [enabledReservationLocations])

  const areaOptions = useMemo(
    () => getAreaOptions(selectedReservationLocation, t),
    [selectedReservationLocation, t],
  )
  const visibleAreas = useMemo(
    () => getVisibleAreas(selectedReservationLocation),
    [selectedReservationLocation],
  )
  const [selectedAreaId, setSelectedAreaId] = useState(ANY_AREA_ID)
  const selectedAreaIdRef = useRef(selectedAreaId)

  useEffect(() => {
    if (!selectedReservationLocation?.id) {
      return
    }

    logger.isLoaded({
      isAddOn: true,
      isPreview,
      pageType: PageType.widget,
      areaSelectionDisplayed: visibleAreas.length > 1,
      numAreas: visibleAreas.length,
      formLayout: effectiveFormLayout,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveFormLayout, isPreview, selectedReservationLocation?.id, visibleAreas.length])

  useEffect(() => {
    const selectedAreaUnavailable =
      selectedAreaId !== ANY_AREA_ID && !areaOptions.some(({id}) => id === selectedAreaId)

    if (selectedAreaUnavailable) {
      selectedAreaIdRef.current = ANY_AREA_ID
      setSelectedAreaId(ANY_AREA_ID)
    }
  }, [areaOptions, selectedAreaId])

  const partySizeOptions = useMemo(
    () => getPartySizeOptionsForReservationLocation(t, selectedReservationLocation),
    [selectedReservationLocation],
  )

  const [selectedPartySize, setSelectedPartySize] = useState(
    getInitialPartySizeFromOptions(partySizeOptions),
  )

  useEffect(() => {
    setSelectedPartySize(getInitialPartySizeFromOptions(partySizeOptions))
  }, [partySizeOptions])

  const [isManualApproval, setIsManualApproval] = useState(false)

  useEffect(() => {
    const manualApprovalConfig =
      selectedReservationLocation?.configuration?.onlineReservations?.approval

    const partySizeThreshold =
      manualApprovalConfig?.manualForLargePartiesOptions?.partySizeThreshold || 0

    const shouldBeManualApprove =
      manualApprovalConfig?.mode === ApprovalMode.MANUAL ||
      (manualApprovalConfig?.mode === ApprovalMode.MANUAL_FOR_LARGE_PARTIES &&
        selectedPartySize >= partySizeThreshold)

    setIsManualApproval(shouldBeManualApprove)
  }, [selectedPartySize, selectedReservationLocation])

  const reservationLocationsOptions: DropdownOptionProps[] = useMemo(
    () => getReservationLocationsOptions(enabledReservationLocations, t),
    [enabledReservationLocations],
  )

  const businessSchedule = useMemo(
    () => getBusinessScheduleFromReservationLocation(selectedReservationLocation),
    [selectedReservationLocation],
  )

  // date
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(() =>
    getNearestWorkingDate({
      businessSchedule,
      regionalSettings,
      timeZone: selectedReservationLocation?.location?.timeZone,
    }),
  )

  // time
  const timeOptions = useMemo(
    () => getTimeOptions({timeSlots: dayTimeSlots, regionalSettings}),
    [dayTimeSlots, regionalSettings],
  )
  const [selectedTime, setSelectedTime] = useState<TimeOption | undefined>(() =>
    selectedDate ? findTimeOption(timeOptions, selectedDate) : undefined,
  )

  const clickOnReservationsAttributesBiParams = useMemo(
    () => ({
      isAddOn: true,
      isMultiLocation: !!enabledReservationLocations.length,
      isPreview,
      locationId: selectedReservationLocation?.location?.id,
      reservationLocationId: selectedReservationLocation?.id,
    }),
    [enabledReservationLocations.length, isPreview, selectedReservationLocation?.id],
  )

  const handleFilterDate = useCallback(
    (date: Date) =>
      filterDate({
        date,
        businessSchedule,
        timeZone: selectedReservationLocation?.location?.timeZone,
        regionalFormat: regionalSettings,
      }),
    [businessSchedule, selectedReservationLocation?.location?.timeZone, regionalSettings],
  )

  const handleLocationChange = (option: DropdownOptionProps) => {
    const newSelectedReservationLocation =
      getReservationLocationById(option.id!, enabledReservationLocations) ??
      enabledReservationLocations[0]

    logger.clickOnReservationsAttributes({
      ...clickOnReservationsAttributesBiParams,
      value: option.id!,
      locationId: newSelectedReservationLocation?.location?.id,
      reservationLocationId: newSelectedReservationLocation?.id,
      fieldName: FieldName.location,
    })

    setSelectedReservationLocation(newSelectedReservationLocation)
    selectedAreaIdRef.current = ANY_AREA_ID
    setSelectedAreaId(ANY_AREA_ID)
    setSelectedTime(undefined)
  }

  const handlePartySizeChange = (option: DropdownOptionProps) => {
    const newSelectedPartySize = Number(option.id)

    logger.clickOnReservationsAttributes({
      ...clickOnReservationsAttributesBiParams,
      value: option.id!,
      fieldName: FieldName.partySize,
    })

    setSelectedPartySize(newSelectedPartySize)
    setSelectedTime(undefined)

    loadDayTimeSlots(selectedDate, newSelectedPartySize)
  }

  const handleDateChange = (date: Date) => {
    logger.clickOnReservationsAttributes({
      ...clickOnReservationsAttributesBiParams,
      value: date.toISOString(),
      fieldName: FieldName.date,
    })

    setSelectedDate(date)
    setSelectedTime(undefined)

    loadDayTimeSlots(date)
  }

  const handleTimeChange = (option: TimeOption) => {
    logger.clickOnReservationsAttributes({
      ...clickOnReservationsAttributesBiParams,
      value: option.data.startDate.toISOString(),
      fieldName: FieldName.time,
    })

    setSelectedTime(option)
  }

  const handleAreaChange = (areaId: string) => {
    logger.clickOnReservationsAttributes({
      ...clickOnReservationsAttributesBiParams,
      value: areaId,
      fieldName: FieldName.area,
    })
    selectedAreaIdRef.current = areaId
    setSelectedAreaId(areaId)
    setSelectedTime(undefined)

    loadDayTimeSlots(selectedDate, selectedPartySize, areaId)
  }

  const handleFindTableSubmit = async (e) => {
    e.preventDefault()

    const isPartySizeExceedsMax = selectedPartySize >= partySizeOptions.length

    readAndSetSubmitButtonContentWidth()

    if (!selectedReservationLocation?.id || !selectedDate || !selectedTime) {
      return null
    }

    const selectedDateAndTime = setHMToDate(
      selectedDate,
      selectedTime.data.hours,
      selectedTime.data.minutes,
    )

    logger.clickOnFindATable({
      datetime: selectedDateAndTime,
      isMultiLocation: !!reservationLocationsOptions.length,
      locationId: selectedReservationLocation?.location?.id,
      reservationLocationId: selectedReservationLocation?.id,
      isPreview,
      partySize: selectedPartySize,
    })

    if (
      !selectedReservationLocation.configuration?.onlineReservations?.onlineReservationsEnabled ||
      !isSiteBusinessPremium
    ) {
      setShouldShowReservationsUnavailable(true)

      return
    }

    setIsLoading(true)

    const normalizedAreaId = normalizeAreaId(selectedAreaId)

    const params = {
      reservationData: {
        startDate: zonedTimeToUtc(
          selectedDateAndTime,
          selectedReservationLocation?.location?.timeZone,
        ),
        partySize: selectedPartySize,
        reservationLocationId: selectedReservationLocation.id!,
        ...(normalizedAreaId ? {areaId: normalizedAreaId} : {}),
      },
    }

    if (
      isManualApproval &&
      !isPartySizeExceedsMax &&
      selectedTime.data.status === Status.AVAILABLE
    ) {
      await goToDetailsPage(params)
    } else {
      await goToReservationsPage(params)
    }
  }

  const loadDayTimeSlots = (
    date?: Date,
    partySize = selectedPartySize,
    areaId = selectedAreaIdRef.current,
  ) => {
    if (!selectedReservationLocation?.id || !date) {
      return
    }

    const normalizedAreaId = normalizeAreaId(areaId)

    getDayTimeSlots({
      reservationLocationId: selectedReservationLocation.id,
      date,
      partySize,
      timeSlotInterval:
        selectedReservationLocation.configuration?.onlineReservations?.timeSlotInterval,
      timeZone: selectedReservationLocation?.location?.timeZone,
      ...(normalizedAreaId ? {areaId: normalizedAreaId} : {}),
    })
  }

  useEffect(() => {
    loadDayTimeSlots(selectedDate)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReservationLocation])

  useEffect(() => {
    const newSelectedTime =
      timeOptions.find(({data}) => data.status === Status.AVAILABLE) ?? timeOptions[0]

    if (newSelectedTime) {
      setSelectedTime(newSelectedTime)
    }
  }, [timeOptions])

  return {
    t,
    isSSR,
    submitButtonContentWidth,
    isMobile,
    regionalSettings,
    reservationLocationsOptions,
    isLoading,
    partySizeOptions,
    timeOptions,
    areaOptions,
    selectedAreaId,
    selectedReservationLocation,
    selectedPartySize,
    selectedDate,
    selectedTime,
    handleLocationChange,
    handlePartySizeChange,
    handleDateChange,
    handleTimeChange,
    handleAreaChange,
    handleFindTableSubmit,
    filterDate: handleFilterDate,
    shouldShowReservationsUnavailable,
    handleHideReservationsUnavailable,
    reservationAddOnDataHookWithId,
  }
}
